import "../../field-container/field-container";
import "../../checkbox/checkbox";
import "../../textfield/phone-field";
import "../../picker/picker";
import "../../alert/alert";
import "../../textfield/textfield";
import "../../divider/divider";
import "../../menu/menu-item";
import "../../card/card";
import "../../progress-circle/progress-circle";

import { CSSResultArray, html, type TemplateResult } from "lit";
import { state } from "lit/decorators.js";
import { msg, str } from "@lit/localize";
import { Dinero } from "dinero.js";

import styles from "./step02-result.scss";
import grid from "../../../styles/layout/_grid.scss";
import margin from "../../../styles/base/_margin.scss";
import padding from "../../../styles/base/_padding.scss";
import { WizardStep } from "../../wizard/wizard-step";
import { getConfiguration } from "../../../utils/configuration";
import { FormController } from "../../../forms/form-controller";
import { FormControl } from "../../../forms/form-control";
import { email, required } from "../../../forms/validators";
import { calculateNotaryReward, NotaryReward } from "../../../data/notary-calculator";
import { createDinero, createDineroFromNumber, dineroToData } from "../../../utils/money";
import { IMAGES_MAP } from "../../../common/images";
import { calculateNotaryEstimation, CalculateNotaryEstimationInput } from "../../../api/notary-estimation/calculate-notary-estimation";
import { SearchLocationItem, searchLocations } from "../../../api/location/search-locations";
import { LocationLevel } from "../../../enums/location-level";
import { NotaryEstimationWizardStep01PropertyValueFormData } from "./step01-property-value";
import { NotaryEstimationWizardNames } from "../notary-estimation-wizard-names";
import { USER_DATA_ID } from "../custom-data";
import { WizardStepSubmittedData } from "../../wizard/events";
import { Logger } from "../../../services/logger";
import { NotaryEstimationWizard } from "../notary-estimation-wizard";

const configuration = getConfiguration();

declare interface FormData {
	district: string;
	name: string;
	email: string;
	phone: string;
	multipleProperties: boolean;
	agree: boolean;
}

export class NotaryEstimationWizardStep02Result extends WizardStep<NotaryEstimationWizard, FormData> {
	public static override get styles(): CSSResultArray {
		return [...super.styles, grid, margin, padding, styles];
	}

	@state()
	private _districts: ReadonlyArray<SearchLocationItem> = [];

	@state()
	private _districtsLoadError: boolean = false;

	private _propertyValue!: Dinero;

	private _reward!: NotaryReward;

	public override connectedCallback(): void {
		super.connectedCallback();

		const data = this.snapshotState.getRequiredStep<NotaryEstimationWizardStep01PropertyValueFormData>(
			NotaryEstimationWizardNames.Step01PropertyValue,
		).form;

		this._propertyValue = createDinero({
			amount: parseInt(data.propertyValue) * 100,
			currency: "CZK",
		});

		this._reward = calculateNotaryReward(this._propertyValue);

		searchLocations({
			level: LocationLevel.District,
			take: 200,
		})
			.then((districts) => {
				this._districts = districts;
			})
			.catch((err) => {
				Logger.error(err);
				this._districtsLoadError = true;
			});
	}

	protected createForm(): FormController<FormData> {
		return new FormController<FormData>(this, {
			district: new FormControl("", [required]),
			name: new FormControl("", [required]),
			email: new FormControl("", [required, email]),
			phone: new FormControl("", [required]),
			multipleProperties: new FormControl<boolean>(false),
			agree: new FormControl(false, [required]),
		});
	}

	protected renderContent(): TemplateResult {
		if (this._districtsLoadError) {
			return html` <oz-alert variant="negative"> ${msg(html`Formulář se nepodařilo načíst. V případě přetrvávajících problémů, prosím kontaktujte naši podporu na <a href="mailto:info@odhad-zdarma.cz">info@odhad-zdarma.cz</a>. Děkujeme.`)} </oz-alert> `;
		}

		if (this._districts.length < 1) {
			return html` <oz-progress-circle></oz-progress-circle> `;
		}

		const price = createDineroFromNumber(configuration.prices.notary.flatOrLand).toFormat("$0,0");

		return html`
			<div class="txt-h">${msg("Odměna notáře z dědictví")}</div>
			<div class="txt-price mb-0">${this._reward.reward.add(this._reward.tax).toFormat()}</div>
			<div class="text-center mb-4">${msg(str`Odměna ${this._reward.reward.toFormat()} + DPH ${this._reward.tax.toFormat()}`)}</div>

			<oz-divider></oz-divider>

			<div class="txt-h mt-4 mb-0">${msg("Je součástí dědictví nemovitost?")}</div>
			<div class="text-center">${msg("Budete potřebovat odhad nemovitosti ve formátu požadovaném notáři.")}</div>

			<div class="row txt-my">
				<div class="d-flex flex-column col-xl-6 txt-mb txt-mb-xl-0">
					<oz-card stretch>
						<picture slot="image-top">
							<source srcset="${IMAGES_MAP.specialistCircle.x1.avif()}, ${IMAGES_MAP.specialistCircle.x2.avif()} 2x" type="image/avif" />
							<source srcset="${IMAGES_MAP.specialistCircle.x1.webp()}, ${IMAGES_MAP.specialistCircle.x2.webp()} 2x" type="image/webp" />
							<source srcset="${IMAGES_MAP.specialistCircle.x1.png()}, ${IMAGES_MAP.specialistCircle.x2.png()} 2x" type="image/png" />
							<img src="${IMAGES_MAP.specialistCircle.x1.png()}" />
						</picture>
						<div slot="heading">${msg(html`Úvodní<br />konzultace`)}</div>
						<span slot="label" class="green">${msg("zdarma")}</span>
						<div>${msg(html`Za telefonické posouzení odhadu nám <b>nic neplatíte.</b>`)}</div>
					</oz-card>
				</div>
				<div class="d-flex flex-column col-xl-6">
					<oz-card stretch>
						<picture slot="image-top">
							<source srcset="${IMAGES_MAP.notaryCircle.x1.avif()}, ${IMAGES_MAP.notaryCircle.x2.avif()} 2x" type="image/avif" />
							<source srcset="${IMAGES_MAP.notaryCircle.x1.webp()}, ${IMAGES_MAP.notaryCircle.x2.webp()} 2x" type="image/webp" />
							<source srcset="${IMAGES_MAP.notaryCircle.x1.png()}, ${IMAGES_MAP.notaryCircle.x2.png()} 2x" type="image/png" />
							<img src="${IMAGES_MAP.notaryCircle.x1.png()}" />
						</picture>
						<div slot="heading">${msg("Odhad v rozsahu požadovaném notáři")}</div>
						<span slot="label" class="blue">${msg("zpoplatněný")}</span>
						<div>${msg(html`Po konzultaci můžete požádat o vyhotovení odhadu pro notáře, který je zpoplatněn částkou od ${price} (dle rozsahu).`)}</div>
					</oz-card>
				</div>
			</div>

			<div class="mb-5">
				<div class="txt-h">${msg("Proč pro tuto službu potřebujeme Vaše kontaktní údaje?")}</div>
				<div class="text-center txt-mb-0">${msg("Tento odhad zpracovává")}</div>
				<div class="txt-h-primary">${msg("KONKRÉTNÍ SPECIALISTA.")}</div>
			</div>

			<oz-field-container>
				<oz-picker label="${msg("Lokalita (okres) jedné z nemovitostí")}" size="l" required ${this._form.attach("district")}> ${this._districts.map((district) => html` <oz-menu-item value="${district.id}">${district.name}</oz-menu-item> `)} </oz-picker>
			</oz-field-container>

			<oz-field-container>
				<oz-textfield label="${msg("Jméno a příjmení")}" required ${this._form.attach("name")}></oz-textfield>
			</oz-field-container>

			<div class="row">
				<div class="col-md-6">
					<oz-field-container>
						<oz-textfield label="${msg("E-mail")}" required type="email" ${this._form.attach("email")}></oz-textfield>
					</oz-field-container>
				</div>
				<div class="col-md-6">
					<oz-field-container>
						<oz-phone-field label="${msg("Telefon")}" required ${this._form.attach("phone")}></oz-phone-field>
					</oz-field-container>
				</div>
			</div>

			<oz-field-container>
				<oz-checkbox ${this._form.attach("multipleProperties")}>${msg("Potřebuji odhad pro více nemovitostí")}</oz-checkbox>
			</oz-field-container>

			<oz-field-container>
				<oz-checkbox required ${this._form.attach("agree")}> ${msg(html`Souhlasím se zpracováním <a href="${configuration.actions["legal/personalData"]}" target="_blank">osobních údajů</a>`)} </oz-checkbox>
			</oz-field-container>
		`;
	}

	protected override getNextButtonLabel(): string {
		return msg("CHCI NEZÁVAZNĚ POPTAT");
	}

	protected override async createAdditionalSubmitData(data: FormData): Promise<Partial<WizardStepSubmittedData<FormData>>> {
		const send: CalculateNotaryEstimationInput = {
			district: data.district,
			name: data.name,
			email: data.email,
			phone: data.phone,
			value: dineroToData(this._propertyValue),
			reward: dineroToData(this._reward.reward),
			rewardTax: dineroToData(this._reward.tax),
			multipleProperties: data.multipleProperties,
		};

		const id = await calculateNotaryEstimation(send);

		return {
			customStepData: {
				[USER_DATA_ID]: id,
			},
			gtmDataLayerItems: [
				{
					event: `${this.configuration.wizardName}_${this.stepDetails.name}`,
					language_code: getConfiguration().languageCode,
					district_code: data.district,
					user_name: data.name,
					user_email: data.email,
					user_phone: data.phone,
					multiple_properties: data.multipleProperties.toString(),
				},
			],
		};
	}
}
